import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Loader2, Lock, MessageSquare, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin-only internal notes per service (GET/POST/DELETE /api/admin/orders/{id}/internal-notes).
 * Stored in their own collection, so clients and drivers never receive them.
 */

// One counts request shared by every card on the dashboard; refreshed after a minute.
const COUNTS_TTL_MS = 60_000;
let countsCache = { at: 0, promise: null };

function loadNoteCounts(backendUrl) {
  const now = Date.now();
  if (countsCache.promise && now - countsCache.at < COUNTS_TTL_MS) return countsCache.promise;
  const token = localStorage.getItem('admin_token');
  countsCache = {
    at: now,
    promise: fetch(`${backendUrl}/api/admin/internal-notes/counts`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : {}))
      .catch(() => ({})),
  };
  return countsCache.promise;
}

export default function AdminInternalNotes({ orderId, backendUrl }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  useEffect(() => {
    if (!orderId || !backendUrl) return undefined;
    let cancelled = false;
    loadNoteCounts(backendUrl).then((counts) => {
      if (!cancelled) setCount(counts?.[orderId] || 0);
    });
    return () => {
      cancelled = true;
    };
  }, [orderId, backendUrl]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/orders/${orderId}/internal-notes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setNotes(list);
      setCount(list.length);
    } catch (e) {
      console.error(e);
      toast.error(t('admin_internal_notes_load_fail'));
    } finally {
      setLoading(false);
    }
  }, [backendUrl, orderId, t]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const addNote = async () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/orders/${orderId}/internal-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const note = await res.json();
      setNotes((prev) => [...prev, note]);
      setCount((c) => c + 1);
      setDraft('');
      countsCache = { at: 0, promise: null };
    } catch (e) {
      console.error(e);
      toast.error(t('admin_internal_notes_save_fail'));
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm(t('admin_internal_notes_delete_confirm'))) return;
    try {
      const res = await fetch(
        `${backendUrl}/api/admin/orders/${orderId}/internal-notes/${noteId}`,
        { method: 'DELETE', headers: authHeaders() },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      setCount((c) => Math.max(0, c - 1));
      countsCache = { at: 0, promise: null };
    } catch (e) {
      console.error(e);
      toast.error(t('admin_internal_notes_delete_fail'));
    }
  };

  if (!orderId) return null;

  const title =
    count > 0 ? t('admin_internal_notes_title_count', { count }) : t('admin_internal_notes_title');
  const dateLocale = i18n.language?.startsWith('en') ? 'en-GB' : 'es-ES';

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={`w-full justify-between h-auto py-2.5 px-3 text-left font-normal ${
          count > 0 ? 'border-blue-300 bg-blue-50 hover:bg-blue-100' : ''
        }`}
        onClick={() => setOpen(true)}
        data-testid={`internal-notes-toggle-${orderId}`}
      >
        <span className="flex items-center gap-2 text-sm text-gray-800">
          <MessageSquare className="h-4 w-4 shrink-0 text-blue-600" />
          {title}
          <Lock className="h-3 w-3 text-gray-400" />
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
      </Button>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50/60 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-blue-900 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          {title}
          <span className="flex items-center gap-1 text-[11px] font-normal text-gray-500">
            <Lock className="h-3 w-3" />
            {t('admin_internal_notes_admin_only')}
          </span>
        </p>
        <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => setOpen(false)}>
          <ChevronUp className="h-4 w-4" />
        </Button>
      </div>

      {loading && notes.length === 0 ? (
        <p className="text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
        </p>
      ) : notes.length === 0 ? (
        <p className="text-sm text-gray-500">{t('admin_internal_notes_empty')}</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((note) => (
            <li key={note.id} className="rounded-md border border-blue-100 bg-white px-3 py-2 text-sm">
              <div className="flex items-start justify-between gap-2">
                <p className="text-[11px] text-gray-500">
                  <span className="font-medium text-gray-700">{note.author_name || '—'}</span>
                  {' · '}
                  {note.created_at ? new Date(note.created_at).toLocaleString(dateLocale) : ''}
                </p>
                {note.can_delete && (
                  <button
                    type="button"
                    onClick={() => deleteNote(note.id)}
                    className="text-gray-400 hover:text-red-600"
                    aria-label="delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <p className="mt-1 whitespace-pre-wrap break-words text-gray-900">{note.text}</p>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) addNote();
          }}
          placeholder={t('admin_internal_notes_placeholder')}
          rows={2}
          maxLength={4000}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={saving || !draft.trim()}
            onClick={addNote}
          >
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {t('admin_internal_notes_add')}
          </Button>
        </div>
      </div>
    </div>
  );
}
